import { Injectable, inject } from '@angular/core';
import { Observable, Subscription, of } from 'rxjs';
import * as CryptoJS from 'crypto-js';
import { isNullOrUndefined } from 'is-what';
import {
  IUserLogin,
  IUserLoginData,
  IUserTokens,
} from '../interfaces/user.interface';
import { UserService } from './user.service';
import { HttpResponse } from '@angular/common/http';
import { jwtDecode } from 'jwt-decode';
import { IToken } from '../interfaces/token.interface';
import { CommonEnum } from '../enums/common.enum';
import { Router } from '@angular/router';
@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private userService = inject(UserService);
  private router = inject(Router);

  /**
   * Return info if user is logged in
   *
   * @return {*}  {Observable<boolean>}
   * @memberof AuthenticationService
   */
  isLoggedIn(): Observable<boolean> {
    return this.getLoggedData() !== null ? of(true) : of(false);
  }

  /**
   * Return token
   *
   * @return {*}  {string}
   * @memberof AuthenticationService
   */
  getToken(): string {
    let token = sessionStorage.getItem(CommonEnum.AccessToken);
    if (isNullOrUndefined(token)) {
      token = '';
    }
    return token as string;
  }

  /**
   * Return refresh token
   *
   * @return {*}  {string}
   * @memberof AuthenticationService
   */
  getRefreshToken(): string {
    let refreshToken = sessionStorage.getItem(CommonEnum.RefreshToken);
    if (isNullOrUndefined(refreshToken)) {
      refreshToken = '';
    }
    return refreshToken as string;
  }

  /**
   * Remove token
   *
   * @private
   * @memberof AuthenticationService
   */
  removeToken(): void {
    sessionStorage.removeItem(CommonEnum.AccessToken);
    sessionStorage.removeItem(CommonEnum.RefreshToken);
  }

  /**
   * The function "getLoggedData" retrieves the login token from the session storage and returns it, or
   * returns null if the token is not found.
   *
   * @private
   * @return {*}  {(string | null)}
   * @memberof AuthenticationService
   */
  private getLoggedData(): string | null {
    const token = this.getToken();
    if (!isNullOrUndefined(token) && token !== '') {
      if (this.validateToken()) {
        return token;
      }
      return null;
    }
    return null;
  }

  /**
   *The function `validateToken` checks if the extracted token has expired or not.
   *
   * @private
   * @return {*}  {boolean}
   * @memberof AuthenticationService
   */
  private validateToken(): boolean {
    const extractToken = this.extractToken();
    if (extractToken.exp < +new Date()) {
      return true;
    }
    return false;
  }

  /**
   * The `login` function takes user login data, hashes the password, sends a login request to the
   * server, and resolves a promise with a boolean indicating whether the login was successful.
   *
   * @param {IUserLoginData} data
   * @return {*}  {Promise<boolean>}
   * @memberof AuthenticationService
   */
  login(data: IUserLoginData): Promise<boolean> {
    return new Promise((resolve) => {
      const hashedPassword = CryptoJS.SHA256(
        CryptoJS.enc.Utf16LE.parse(data.password),
      )
        .toString(CryptoJS.enc.Hex)
        .toLowerCase();
      data.password = hashedPassword;
      const subscription: Subscription = this.userService
        .login(data)
        .subscribe((data: HttpResponse<IUserLogin>) => {
          const tokens = data.body?.result ?? null;
          this.storeTokens(tokens);
          resolve(true);
          subscription.unsubscribe();
        });
    });
  }

  /**
   * The function extracts and decodes a JSON Web Token (JWT) from a token string.
   *
   * @return {*}  {IToken}
   * @memberof AuthenticationService
   */
  extractToken(): IToken {
    const token = this.getToken();

    const extractedToken: IToken = jwtDecode(token);
    extractedToken.role =
      extractedToken[
        'http://schemas.microsoft.com/ws/2008/06/identity/claims/role'
      ];
    return extractedToken;
  }

  /**
   * The `logout()` function is responsible for logging out the user. It removes the token from the
   * session storage, effectively ending the user's session.
   *
   * @return {*}  {Promise<boolean>}
   * @memberof AuthenticationService
   */
  logout(): Promise<boolean> {
    return new Promise((resolve) => {
      const token = this.getToken();
      const refreshToken = this.getRefreshToken();
      if (!isNullOrUndefined(token)) {
        const subscription: Subscription = this.userService
          .logout(refreshToken)
          .subscribe((data: HttpResponse<unknown>) => {
            if (data) {
              resolve(true);
              subscription.unsubscribe();
              this.removeToken();
            }
          });
      }
    });
  }

  private storeTokens(tokens: IUserTokens | null): void {
    if (!isNullOrUndefined(tokens)) {
      sessionStorage.setItem(CommonEnum.AccessToken, tokens.accessToken);
      sessionStorage.setItem(CommonEnum.RefreshToken, tokens.refreshToken);
    } else {
      this.router.navigate(['']);
    }
  }

  storeAccessToken(token: string): void {
    sessionStorage.setItem(CommonEnum.AccessToken, token);
  }
}
